import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import {
  ApiClient,
  getWebhookConfig,
  updateWebhookConfig,
  rotateWebhookSecret,
} from '../services/api';
import type { WebhookConfigData } from '../services/api';

const PURPLE = '#250e62';
const LIGHT_COOL_GREY = '#EFF3F5';

function SecretRevealDialog({
  open,
  secret,
  onClose,
  onCopied,
}: {
  open: boolean;
  secret: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    onCopied();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Webhook Signing Secret</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copy this secret now. It will not be shown again.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: 14,
              wordBreak: 'break-all',
              flex: 1,
            }}
          >
            {secret}
          </Typography>
          <Tooltip title="Copy to clipboard">
            <IconButton size="small" onClick={handleCopy}>
              <i className="fa-thin fa-copy" style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Each webhook POST includes an X-Sophia-Signature header: sha256=HMAC-SHA256(secret, raw
          request body) in hex. Verify it before trusting a delivery.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function WebhookConfig(): React.ReactElement | null {
  const { idToken, hasRole } = useAuth();
  // SOPHIA-35120: internal users viewing a partner see webhook config read-only
  // SOPHIA-35121: activePartnerId scopes the GET to the switcher selection
  const { isInternal, activePartnerId } = usePartnerScope();
  const readOnly = isInternal;
  const client = useMemo(() => new ApiClient(idToken), [idToken]);

  const [config, setConfig] = useState<WebhookConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [showPayload, setShowPayload] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebhookConfig(client, activePartnerId);
      setConfig(data.config);
      setEnabled(data.config?.enabled ?? false);
      setUrl(data.config?.url ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load webhook config');
    } finally {
      setLoading(false);
    }
  }, [client, activePartnerId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  if (!hasRole('api_developer') && !hasRole('sophia_admin')) {
    return null;
  }

  const validateUrl = (value: string): boolean => {
    if (!value) {
      setUrlError('URL is required when webhook is enabled');
      return false;
    }
    if (!value.startsWith('https://')) {
      setUrlError('URL must use HTTPS');
      return false;
    }
    setUrlError(null);
    return true;
  };

  const handleToggle = async (newEnabled: boolean) => {
    if (newEnabled && !url) {
      setEnabled(true);
      return;
    }

    if (newEnabled && !validateUrl(url)) {
      setEnabled(true);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const data = await updateWebhookConfig(
        { enabled: newEnabled, url: newEnabled ? url : undefined },
        client
      );
      setConfig(data.config);
      setEnabled(data.config?.enabled ?? false);
      setUrl(data.config?.url ?? '');
      if (data.signing_secret) {
        setRevealedSecret(data.signing_secret);
      }
      setSnackbar(newEnabled ? 'Webhook enabled' : 'Webhook disabled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update webhook config');
      setEnabled(!newEnabled);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!validateUrl(url)) return;

    setSaving(true);
    setError(null);
    try {
      const data = await updateWebhookConfig({ enabled, url }, client);
      setConfig(data.config);
      setEnabled(data.config?.enabled ?? false);
      setUrl(data.config?.url ?? '');
      if (data.signing_secret) {
        setRevealedSecret(data.signing_secret);
      }
      setSnackbar('Webhook configuration saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save webhook config');
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    setRotateConfirmOpen(false);
    setRotating(true);
    setError(null);
    try {
      const data = await rotateWebhookSecret(client);
      setConfig(data.config);
      if (data.signing_secret) {
        setRevealedSecret(data.signing_secret);
      }
      setSnackbar('Signing secret rotated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate signing secret');
    } finally {
      setRotating(false);
    }
  };

  const hasUnsavedUrl = url !== (config?.url ?? '');

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const lastDelivered = config?.last_delivered_at;

  return (
    <>
      <Box
        sx={{
          mt: 4,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: LIGHT_COOL_GREY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className="fa-thin fa-satellite-dish" style={{ fontSize: 16, color: PURPLE }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Webhook Notifications
            </Typography>
            <Typography variant="caption" color="text.secondary">
              We check for new course completions every 5 minutes and send a POST to your URL for
              each new transcript.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControlLabel
          sx={{ mt: 1.5, ml: 0 }}
          control={
            <Switch
              checked={enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={saving || readOnly}
              size="small"
            />
          }
          label={<Typography variant="body2">Enable webhook notifications</Typography>}
        />

        {enabled && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Webhook URL"
                placeholder="https://api.yoursite.com/webhook"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setUrlError(null);
                }}
                error={!!urlError}
                helperText={urlError}
                fullWidth
                size="small"
                disabled={saving || readOnly}
              />
              {!readOnly && (
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedUrl}
                  sx={{ minWidth: 80, height: 40 }}
                >
                  {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              )}
            </Box>

            {config?.url && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1.5, display: 'block' }}
              >
                <i className="fa-thin fa-clock" style={{ fontSize: 12, marginRight: 4 }} />
                {lastDelivered
                  ? `Last delivered: ${new Date(lastDelivered).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                  : 'No deliveries yet — webhooks are sent when new transcripts are available.'}
              </Typography>
            )}

            {config?.has_signing_secret && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <i className="fa-thin fa-key" style={{ fontSize: 12, color: PURPLE }} />
                <Typography variant="caption" color="text.secondary">
                  Deliveries are signed with your signing secret (X-Sophia-Signature header).
                </Typography>
                {!readOnly && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setRotateConfirmOpen(true)}
                    disabled={rotating || saving}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', p: 0, minWidth: 0 }}
                  >
                    {rotating ? 'Rotating…' : 'Rotate secret'}
                  </Button>
                )}
              </Box>
            )}

            <Typography
              variant="caption"
              onClick={() => setShowPayload(!showPayload)}
              sx={{
                mt: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <i
                className={showPayload ? 'fa-thin fa-chevron-down' : 'fa-thin fa-chevron-right'}
                style={{ fontSize: 10 }}
              />
              Example payload
            </Typography>

            <Collapse in={showPayload}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: LIGHT_COOL_GREY,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  whiteSpace: 'pre',
                }}
              >
                {`POST ${url || 'https://api.yoursite.com/webhook'}
Content-Type: application/json
X-Sophia-Signature: sha256=<HMAC-SHA256 of the raw body, hex>

{
  "event": "transcript.created",
  "timestamp": "2026-04-08T14:30:00Z",
  "data": {
    "user_id": 12345,
    "transcript_id": 67890
  }
}`}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                One notification is sent per completed course. Deliveries are at-least-once and in
                certification order; verify the X-Sophia-Signature header with your signing secret.
                Use the Transcript API to download the full transcript.
              </Typography>
            </Collapse>
          </Box>
        )}
      </Box>

      <Dialog open={rotateConfirmOpen} onClose={() => setRotateConfirmOpen(false)}>
        <DialogTitle>Rotate signing secret?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The current secret stops working immediately. Deliveries will be signed with the new
            secret, so update your receiver before rotating.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRotate} variant="contained" color="warning">
            Rotate
          </Button>
        </DialogActions>
      </Dialog>

      <SecretRevealDialog
        open={!!revealedSecret}
        secret={revealedSecret ?? ''}
        onClose={() => setRevealedSecret(null)}
        onCopied={() => setSnackbar('Copied to clipboard')}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </>
  );
}
